import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, User, Phone, Home, Building2, Mail, Shield, Car, Bike } from "lucide-react";
import VehiclesManager from "@/components/VehiclesManager";
import { parseVehicles, formatVehiclesForPayload } from "@/lib/vehicles";

export default function EditProfileModal({ open, onClose }) {
    const { user, refresh } = useAuth();
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [tower, setTower] = useState("");
    const [flatNo, setFlatNo] = useState("");
    const [vehicles, setVehicles] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user && open) {
            setName(user.name || "");
            setPhone(user.phone || "");
            setTower(user.tower || "");
            setFlatNo(user.flat_no || "");
            setVehicles(parseVehicles(user.vehicles));
        }
    }, [user, open]);

    if (!open) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanPhone = phone.replace(/\D/g, "").slice(-10);
        if (cleanPhone.length !== 10) {
            toast.error("Please enter a valid 10-digit mobile number");
            return;
        }
        setSaving(true);
        try {
            await api.put("/auth/profile", {
                name: name.trim(),
                phone: cleanPhone,
                tower: user?.role === "resident" ? tower.trim().toUpperCase() : undefined,
                flat_no: user?.role === "resident" ? flatNo.trim() : undefined,
                vehicles: user?.role === "resident" ? formatVehiclesForPayload(vehicles) : undefined,
            });
            await refresh();
            toast.success("Profile updated successfully");
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update profile");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/50 p-4 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div
                className="bg-white rounded-2xl border border-nest-line w-full max-w-lg p-6 sm:p-8 max-h-[92vh] overflow-y-auto shadow-2xl relative animate-scale-up"
                onClick={(e) => e.stopPropagation()}
                data-testid="edit-profile-modal"
            >
                <div className="flex items-center justify-between pb-4 border-b border-nest-line">
                    <div>
                        <p className="nest-stat-label">Account settings</p>
                        <h2 className="font-display text-2xl tracking-tight mt-0.5">Edit profile</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-nest-alt text-nest-ink2 hover:text-nest-ink transition"
                        data-testid="edit-profile-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Account card banner */}
                <div className="mt-5 p-4 rounded-xl bg-nest-alt/60 border border-nest-line flex items-center gap-4">
                    {user?.picture ? (
                        <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-sm" />
                    ) : (
                        <div className="h-14 w-14 rounded-full bg-nest-ink text-white font-display text-xl grid place-items-center shadow-sm">
                            {user?.name?.slice(0, 1) || "U"}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium truncate text-nest-ink">{user?.name}</p>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-nest-clay/15 text-nest-clay inline-flex items-center gap-1">
                                <Shield size={10} />
                                {user?.role}
                            </span>
                        </div>
                        <p className="text-xs text-nest-ink3 flex items-center gap-1 mt-0.5 truncate">
                            <Mail size={12} />
                            {user?.email}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 flex items-center gap-1.5 mb-1.5">
                            <User size={14} className="text-nest-clay" /> Full Name
                        </label>
                        <input
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-1 focus:ring-nest-clay outline-none bg-white text-nest-ink transition text-sm"
                            placeholder="Enter your name"
                            data-testid="edit-profile-name-input"
                        />
                    </div>

                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 flex items-center gap-1.5 mb-1.5">
                            <Phone size={14} className="text-nest-clay" /> Phone Number *
                        </label>
                        <input
                            type="tel"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-1 focus:ring-nest-clay outline-none bg-white text-nest-ink transition text-sm"
                            placeholder="e.g. 9876543210"
                            data-testid="edit-profile-phone-input"
                        />
                    </div>

                    {user?.role === "resident" && (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 flex items-center gap-1.5 mb-1.5">
                                    <Building2 size={14} className="text-nest-clay" /> Tower / Block *
                                </label>
                                <select
                                    required
                                    value={tower}
                                    onChange={(e) => setTower(e.target.value)}
                                    className="w-full px-3.5 py-2.5 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-1 focus:ring-nest-clay outline-none bg-white text-nest-ink transition text-sm cursor-pointer"
                                    data-testid="edit-profile-tower-input"
                                >
                                    <option value="" disabled>Select Tower</option>
                                    <option value="A">Tower A</option>
                                    <option value="B">Tower B</option>
                                    <option value="C">Tower C</option>
                                    <option value="D">Tower D</option>
                                    <option value="E">Tower E</option>
                                    <option value="F">Tower F</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 flex items-center gap-1.5 mb-1.5">
                                    <Home size={14} className="text-nest-clay" /> Flat No.
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={flatNo}
                                    onChange={(e) => setFlatNo(e.target.value)}
                                    className="w-full px-3.5 py-2.5 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-1 focus:ring-nest-clay outline-none bg-white text-nest-ink transition text-sm"
                                    placeholder="e.g. 601"
                                    data-testid="edit-profile-flat-input"
                                />
                            </div>
                        </div>
                    )}

                    {user?.role === "resident" && (
                        <div className="pt-1">
                            <VehiclesManager
                                vehicles={vehicles}
                                onChange={setVehicles}
                                label="Cars / Bikes Numbers"
                                helperText="Add all your cars & bikes for gate security."
                            />
                        </div>
                    )}

                    <div className="pt-4 flex items-center justify-end gap-3 border-t border-nest-line mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 rounded-xl border border-nest-line hover:bg-nest-alt text-sm font-medium text-nest-ink transition"
                            data-testid="edit-profile-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-5 py-2.5 rounded-xl bg-nest-clay text-white hover:bg-nest-clayDark text-sm font-semibold transition shadow-sm disabled:opacity-50"
                            data-testid="edit-profile-submit"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
